import { downloadFile, getFileInfo } from '@chatwoot/utils';

const isLikelyCorsError = error => {
  if (!error) return false;
  if (error instanceof TypeError) return true;
  const message = error.message?.toLowerCase?.() || '';
  return message.includes('failed to fetch') || message.includes('networkerror');
};

const buildFallbackFilename = ({ url, providedName, extension }) => {
  if (providedName) return providedName;

  const { name, type } = getFileInfo(url);
  if (name && name !== 'Unknown File') {
    return name;
  }

  const sanitizedExtension = (extension || type || 'bin').toString().replace(/^\./, '');
  return `attachment_${Date.now()}.${sanitizedExtension}`;
};

const buildAttachmentDispositionUrl = (url, filename) => {
  if (!url || !filename) {
    return url;
  }

  try {
    const baseOrigin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : undefined;

    const parsedUrl = new URL(url, baseOrigin);
    const sanitizedFilename = filename.replace(/["'\\/]/g, '');

    if (parsedUrl.pathname.includes('/rails/active_storage/blobs/redirect/')) {
      parsedUrl.pathname = parsedUrl.pathname.replace(
        '/rails/active_storage/blobs/redirect/',
        '/rails/active_storage/blobs/proxy/'
      );
      parsedUrl.searchParams.set('disposition', 'attachment');
      parsedUrl.searchParams.set('filename', sanitizedFilename);
      return parsedUrl.toString();
    }

    // For other storage URLs (e.g. GCS) try to force attachment disposition.
    parsedUrl.searchParams.set(
      'response-content-disposition',
      `attachment; filename="${sanitizedFilename}"`
    );

    return parsedUrl.toString();
  } catch (error) {
    // If URL constructor fails (e.g. data URLs), just return original URL.
    console.warn('Unable to attach disposition to download URL', error);
    return url;
  }
};

const triggerNativeDownload = (url, filename) => {
  if (typeof document === 'undefined') {
    return false;
  }

  const anchor = Object.assign(document.createElement('a'), {
    href: url,
    download: filename,
    style: 'display: none',
    rel: 'noopener',
  });

  const container = document.body || document.documentElement;
  container.append(anchor);
  anchor.click();
  anchor.remove();

  return true;
};

export const safeDownloadFile = async ({
  url,
  type,
  extension = null,
  filename,
}) => {
  if (!url || !type) {
    throw new Error('Invalid download parameters');
  }

  const resolvedFilename = buildFallbackFilename({
    url,
    providedName: filename,
    extension,
  });

  const downloadUrl = buildAttachmentDispositionUrl(url, resolvedFilename);

  const initiated = triggerNativeDownload(downloadUrl, resolvedFilename);

  if (initiated) {
    return;
  }

  try {
    await downloadFile({ url, type, extension });
  } catch (error) {
    if (!isLikelyCorsError(error)) {
      throw error;
    }

    triggerNativeDownload(downloadUrl, resolvedFilename);
  }
};
